import { Transition } from "@headlessui/react";
import { ColorSwatchIcon, RssIcon } from "@heroicons/react/outline";
import { ChevronRightIcon, ClockIcon } from "@heroicons/react/solid";
import { capitalize } from "lodash";
import Tooltip from "rc-tooltip";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  matchPath,
  Route,
  RouteComponentProps,
  Switch
} from "react-router-dom";
import MapAppearanceSettingsSidebar from "../../components/appearanceSettings/MapAppearanceSettingsSidebar";
import PieChartIcon from "../../components/icons/PieChartIcon";
import PlusIcon from "../../components/icons/PlusIcon";
import { useAppearance } from "../../context/appearance";
import { useAuthentication } from "../../context/authentication";
import { useBillingPlans } from "../../context/billingPlans";
import { FilteredListProvider } from "../../context/filteredList";
import { useInstalls } from "../../context/installs";
import { useLineOfSight } from "../../context/lineOfSight";
import { useLocations } from "../../context/locations";
import { isHotspotLocation } from "../../context/locations/LocationFormSidebar";
import { useMembers } from "../../context/members/MembersProvider";
import { useToast } from "../../context/toast";
import { useViewport } from "../../context/viewport";
import { useWorkspaces } from "../../context/workspaces/WorkspacesProvider";
import AppShell from "../../layout/AppShell";
import PrivateRoute from "../../layout/PrivateRoute";
import classNames from "../../utils/classNames";
import { breakpoints } from "../../utils/constants";
import { PlanType } from "../../utils/types";
import usePrevious from "../../utils/usePrevious";
import ContactFormSidebar from "../Workspace/ContactsData/ContactFormSidebar";
import ContactDetails from "./ContactDetails";
import Dashboard from "./Dashboard";
import HexDetails from "./HexDetails";
import HotspotDetails from "./HotspotDetails";
import InstallDetails from "./InstallDetails";
import LineOfSight from "./LineOfSight";
import LocationDetails from "./LocationDetails";
import MakerDetails from "./MakerDetails";
import Map from "./Map";
import SideMenu from "./SideMenu";
import useSideMenu from "./SideMenu/useSideMenu";
import TimeTravel from "./TimeTravel";

interface Props extends RouteComponentProps<{}> {}

const MapPage: React.FC<Props> = (props) => {
  const { wrapActionWithAuth } = useAuthentication();
  const mapContainerRef = useRef<HTMLElement | null>(null);
  const showSideMenu = useSideMenu((state) => state.showSideMenu);
  const toggleSideMenu = useSideMenu((state) => state.toggleSideMenu);
  const { toggleInstallSidebar } = useInstalls();
  const { startPoint, isMinimized } = useLineOfSight();
  const { getPlanByType, setNewPlan } = useBillingPlans();
  const { width } = useViewport();
  const { isDark } = useAppearance();
  const { currentWorkspaceIsLocked } = useWorkspaces();
  const { membersLimitExceeded } = useMembers();
  const isMobile = useMemo(() => width < breakpoints.md, [width]);
  const detailsPageMatch = matchPath<{
    tab:
      | "hotspots"
      | "locations"
      | "hexes"
      | "contacts"
      | "installs"
      | "makers";
    id: string;
    section: string;
  }>(props.location.pathname, {
    path: `/:tab/:id/:section`
  });
  const pathMatch = matchPath<{
    tab:
      | "hotspots"
      | "locations"
      | "hexes"
      | "contacts"
      | "installs"
      | "makers";
    id?: string;
  }>(props.location.pathname, {
    path: [":tab/:id", "/:tab"]
  });
  const mapDashboardPageMatch = matchPath(props.location.pathname, {
    path: `/hotspots/dashboard`
  });
  const timeTravelPageMatch = matchPath(props.location.pathname, {
    path: "/hotspots/time-travel"
  });
  const lineOfSightPageMatch = matchPath(props.location.pathname, {
    path: "/:tab/line-of-sight"
  });
  const isDetailPage = useMemo(
    () =>
      !!detailsPageMatch?.params.id ||
      !!mapDashboardPageMatch ||
      !!timeTravelPageMatch ||
      !!lineOfSightPageMatch,
    [
      detailsPageMatch?.params.id,
      lineOfSightPageMatch,
      mapDashboardPageMatch,
      timeTravelPageMatch
    ]
  );
  const [openLayersSidebar, setOpenLayersSidebar] = useState(false);
  const [openContactFormSidebar, setOpenContactFormSidebar] = useState(false);
  const {
    setEditLocationConfig,
    selectPointsOnMapConfig,
    setSelectPointsOnMapConfig
  } = useLocations();
  const { currentWorkspace } = useWorkspaces();
  const { setModalUpgradeConfig } = useToast();

  const prevDetailPageId = usePrevious(detailsPageMatch?.params.id);
  useEffect(() => {
    if (
      mapContainerRef.current &&
      detailsPageMatch?.params.id &&
      detailsPageMatch?.params.id !== prevDetailPageId
    ) {
      mapContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [detailsPageMatch, prevDetailPageId]);

  const mapContainerStyle = useMemo(
    () => getMapContainerStyle(isDetailPage, isMobile, isDark, showSideMenu),
    [isDetailPage, isMobile, isDark, showSideMenu]
  );

  const renderMapMenuButtons = useMemo(() => {
    if (
      mapDashboardPageMatch ||
      timeTravelPageMatch ||
      isDetailPage ||
      isMinimized
    ) {
      return null;
    }

    const renderLineOfSightButton = () => {
      if (selectPointsOnMapConfig) return null;
      return (
        <Tooltip placement="left" overlay="Line of sight">
          <button
            type="button"
            onClick={wrapActionWithAuth(() => {
              if (!currentWorkspace?.permissions["time-travel-simulations"]) {
                setModalUpgradeConfig({
                  open: true,
                  resolve: () => {
                    setNewPlan((prevState) => ({
                      ...prevState,
                      plan: getPlanByType(PlanType.pro)
                    }));
                  },
                  nextPlan: getPlanByType(PlanType.pro),
                  featureTitle: "Line of sight",
                  featureDescription:
                    "This feature is only available in workspaces with a pro plan or higher. Upgrade your plan to unlock this feature."
                });
                return;
              }

              setSelectPointsOnMapConfig({
                coordinates: startPoint,
                lineOfSightContext: {
                  pointType: "start"
                }
              });
            })}
            className="z-10 absolute right-8 bottom-24 items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none dark:bg-gray-800 dark:text-gray-200 dark:hover:text-white dark:ring-1 dark:ring-inset dark:ring-gray-200"
          >
            <RssIcon className="h-8 w-8" />
          </button>
        </Tooltip>
      );
    };

    const renderLayersButton = () => {
      if (selectPointsOnMapConfig) return null;
      return (
        <Tooltip placement="left" overlay="Layers">
          <button
            type="button"
            onClick={() => {
              setOpenLayersSidebar(true);
            }}
            className="z-10 absolute right-8 bottom-8 items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none dark:bg-gray-800 dark:text-gray-200 dark:hover:text-white dark:ring-1 dark:ring-inset dark:ring-gray-200"
          >
            <ColorSwatchIcon className="h-8 w-8" />
          </button>
        </Tooltip>
      );
    };

    if (pathMatch?.params.tab === "hexes") {
      return (
        <>
          {renderLineOfSightButton()}
          {renderLayersButton()}
        </>
      );
    }

    if (pathMatch?.params.tab === "hotspots") {
      return (
        <>
          <Tooltip placement="left" overlay="Time travel">
            <button
              type="button"
              onClick={wrapActionWithAuth(() => {
                if (!currentWorkspace?.permissions["time-travel-simulations"]) {
                  setModalUpgradeConfig({
                    open: true,
                    resolve: () => {
                      setNewPlan((prevState) => ({
                        ...prevState,
                        plan: getPlanByType(PlanType.pro)
                      }));
                    },
                    nextPlan: getPlanByType(PlanType.pro),
                    featureTitle: "Time Travel Simulations",
                    featureDescription:
                      "This feature is only available in workspaces with a pro plan or higher. Upgrade your plan to unlock this feature."
                  });
                  return;
                }

                props.history.push("/hotspots/time-travel");
              })}
              className="z-10 absolute right-8 bottom-56 items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none dark:bg-gray-800 dark:text-gray-200 dark:hover:text-white dark:ring-1 dark:ring-inset dark:ring-gray-200"
            >
              <ClockIcon className="h-8 w-8" />
            </button>
          </Tooltip>

          <Tooltip placement="left" overlay="Region dashboard">
            <button
              type="button"
              onClick={wrapActionWithAuth(() => {
                if (!currentWorkspace?.permissions["regional-dashboard"]) {
                  setModalUpgradeConfig({
                    open: true,
                    resolve: () => {
                      setNewPlan((prevState) => ({
                        ...prevState,
                        plan: getPlanByType(PlanType.pro)
                      }));
                    },
                    nextPlan: getPlanByType(PlanType.pro),
                    featureTitle: "Region dashboard",
                    featureDescription:
                      "This feature is only available in workspaces with a pro plan or higher. Upgrade your plan to unlock this feature."
                  });
                  return;
                }

                props.history.push("/hotspots/dashboard");
              })}
              className="z-10 absolute right-8 bottom-40 items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none dark:bg-gray-800 dark:text-gray-200 dark:hover:text-white dark:ring-1 dark:ring-inset dark:ring-gray-200"
            >
              <PieChartIcon className="h-8 w-8" />
            </button>
          </Tooltip>
          {renderLineOfSightButton()}
          {renderLayersButton()}
        </>
      );
    }

    if (pathMatch?.params.tab === "locations" && !selectPointsOnMapConfig) {
      return (
        <>
          {!currentWorkspaceIsLocked && !membersLimitExceeded && (
            <Tooltip placement="left" overlay="Add location">
              <button
                type="button"
                onClick={wrapActionWithAuth(() => {
                  setEditLocationConfig({
                    show: true,
                    onSuccess: (location) =>
                      isHotspotLocation(location) &&
                      props.history.push(
                        `/locations/${location.uuid}/super-simulations`
                      )
                  });
                })}
                className="z-10 absolute right-8 bottom-40 items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none dark:bg-gray-800 dark:text-gray-200 dark:hover:text-white dark:ring-1 dark:ring-inset dark:ring-gray-200"
              >
                <PlusIcon className="h-8 w-8" />
              </button>
            </Tooltip>
          )}
          {renderLineOfSightButton()}
          {renderLayersButton()}
        </>
      );
    }

    if (pathMatch?.params.tab === "contacts") {
      return (
        <>
          <ContactFormSidebar
            open={openContactFormSidebar}
            toggleEditContactForm={(contact) => {
              setOpenContactFormSidebar(false);

              if (contact?.uuid) {
                props.history.push(`/contacts/${contact.uuid}/info`);
              }
            }}
          />

          {!currentWorkspaceIsLocked && !membersLimitExceeded && (
            <Tooltip placement="left" overlay="Add contact">
              <button
                type="button"
                onClick={wrapActionWithAuth(() =>
                  setOpenContactFormSidebar(true)
                )}
                className="z-10 absolute right-8 bottom-40 items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none dark:bg-gray-800 dark:text-gray-200 dark:hover:text-white dark:ring-1 dark:ring-inset dark:ring-gray-200"
              >
                <PlusIcon className="h-8 w-8" />
              </button>
            </Tooltip>
          )}
          {renderLineOfSightButton()}
          {renderLayersButton()}
        </>
      );
    }

    if (pathMatch?.params.tab === "installs") {
      return (
        <>
          {!currentWorkspaceIsLocked && !membersLimitExceeded && (
            <Tooltip placement="left" overlay="Add install">
              <button
                type="button"
                onClick={wrapActionWithAuth(() => toggleInstallSidebar())}
                className="z-10 absolute right-8 bottom-40 items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none dark:bg-gray-800 dark:text-gray-200 dark:hover:text-white dark:ring-1 dark:ring-inset dark:ring-gray-200"
              >
                <PlusIcon className="h-8 w-8" />
              </button>
            </Tooltip>
          )}
          {renderLineOfSightButton()}
          {renderLayersButton()}
        </>
      );
    }

    return null;
  }, [
    currentWorkspace?.permissions,
    currentWorkspaceIsLocked,
    getPlanByType,
    isDetailPage,
    isMinimized,
    mapDashboardPageMatch,
    membersLimitExceeded,
    openContactFormSidebar,
    pathMatch?.params.tab,
    props.history,
    selectPointsOnMapConfig,
    setEditLocationConfig,
    setModalUpgradeConfig,
    setNewPlan,
    setSelectPointsOnMapConfig,
    startPoint,
    timeTravelPageMatch,
    toggleInstallSidebar,
    wrapActionWithAuth
  ]);

  return (
    <FilteredListProvider>
      <AppShell
        {...props}
        className="h-screen"
        currentPage={
          <div className="flex items-center">
            <span>Map</span>
            <ChevronRightIcon
              className="flex-shrink-0 h-4 w-4 text-gray-400 mx-1"
              aria-hidden="true"
            />
            <span>{capitalize(pathMatch?.params.tab)}</span>
          </div>
        }
      >
        <Transition show={showSideMenu}>
          <div className="xl:hidden">
            <div className="fixed inset-0 flex z-40">
              <Transition.Child
                enter="transition-opacity ease-linear duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity ease-linear duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                className="fixed inset-0"
              >
                <div
                  className="absolute inset-0 bg-gray-600 opacity-75"
                  onClick={toggleSideMenu}
                />
              </Transition.Child>

              <Transition.Child
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
                className="relative flex-1 flex flex-col max-w-full w-full bg-white dark:bg-gray-800 focus:outline-none sm:max-w-xs"
              >
                <div className="absolute top-0 right-0 pt-1.5 -mr-1">
                  <button
                    type="button"
                    className="mr-3 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white sm:ml-1 sm:mr-0"
                    onClick={toggleSideMenu}
                  >
                    <span className="sr-only">Close sidebar</span>
                    {/* Heroicon name: outline/x  */}
                    <svg
                      className="h-6 w-6 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <SideMenu />
              </Transition.Child>

              <div
                className="hidden sm:block flex-shrink-0 w-14"
                aria-hidden="true"
              >
                {/* Dummy element to force sidebar to shrink to fit close icon */}
              </div>
            </div>
          </div>
        </Transition>

        <button
          className={classNames(
            "absolute z-10 top-[6.6rem] transition-all shadow-xl p-3 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-hotspotty-400 bg-white rounded-r-md border border-gray-300 border-l-0 dark:bg-gray-800 dark:text-gray-200 dark:hover:text-white",
            !!selectPointsOnMapConfig && "hidden",
            showSideMenu && "ml-96"
          )}
          onClick={toggleSideMenu}
        >
          <span className="sr-only">Open sidebar</span>
          <svg
            className="h-6 w-6"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M4 6h16M4 12h8m-8 6h16"
            />
          </svg>
        </button>

        <div className="flex-1 relative z-0 flex overflow-hidden">
          <main
            id="default-page-container"
            className="flex-1 relative z-0 overflow-y-auto focus:outline-none xl:order-last"
            tabIndex={0}
            ref={mapContainerRef}
          >
            {renderMapMenuButtons}

            <Map containerStyle={mapContainerStyle} />

            <Switch>
              <PrivateRoute
                path="/:tab/line-of-sight"
                component={LineOfSight}
              />
              <PrivateRoute path="/hotspots/dashboard" component={Dashboard} />
              <PrivateRoute
                path="/hotspots/time-travel"
                component={TimeTravel}
              />
              <Route path="/hotspots/makers/:id" component={MakerDetails} />
              <Route
                path={["/hotspots/statistics", "/hotspots/makers"]}
                render={() => null}
              />
              <Route path="/hotspots/:id" component={HotspotDetails} />
              <Route path="/locations/:id" component={LocationDetails} />
              <Route path="/contacts/:id" component={ContactDetails} />
              <Route path="/installs/:id" component={InstallDetails} />
              <Route path="/hexes/:h3Index" component={HexDetails} />
            </Switch>
          </main>
          <aside
            className={classNames(
              "hidden order-first xl:flex xl:flex-col flex-shrink-0 w-96 border-r border-gray-200 dark:border-gray-500 transition-all",
              showSideMenu ? "relative left-0" : "left-[-400px] absolute"
            )}
          >
            {!!selectPointsOnMapConfig && (
              <div className="absolute inset-0 bg-gray-600 opacity-75 z-50 transition"></div>
            )}
            {showSideMenu && <SideMenu />}
          </aside>
        </div>
        <MapAppearanceSettingsSidebar
          open={openLayersSidebar}
          onClose={() => setOpenLayersSidebar(false)}
        />
      </AppShell>
    </FilteredListProvider>
  );
};

export default MapPage;

//
// Utils
//

const getMapContainerStyle = (
  isDetailPage: boolean,
  isMobile: boolean,
  isDark: boolean,
  showSideMenu: boolean
) => {
  const defaultContainerStyle = {
    backgroundColor: isDark ? "#1f2937" : "#f7f8f8",
    width: showSideMenu ? "auto" : "100%"
  };

  if (isDetailPage) {
    return { ...defaultContainerStyle, height: isMobile ? "20rem" : "60%" };
  }
  return defaultContainerStyle;
};
